import {
  Avatar,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  HStack,
  Spacer,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useMediaQuery,
} from "@chakra-ui/react";
import ProfileView from "./components/ProfileView";
import WallpapersView from "./components/WallpapersView";
import React, { useEffect, useState } from "react";
import { useSettingsContext } from "../../contexts/SettingsContext";
import { Close } from "../../components/APAssetAtlas";
import APIconButton from "../../components/APIconButton";
import { useVocabIntl } from "../../hooks/useVocabTranslation";
import { IExerciserSDK } from "ap-exerciser-sdk";

interface ProfileInterface {
  sdk: IExerciserSDK | undefined;
  sessionData: any;
}

function Profile(props: ProfileInterface) {
  const { sdk, sessionData } = props;

  const { locale, getFormattedLevel, getFormattedString, APFormattedMessage } = useVocabIntl();

  const {
    profileData,
    setProfileData,
    profileOpen,
    setProfileOpen,
    defaultProfileTab,
    setSettingsTabIndex,
    avatar,
    setAvatar
  } = useSettingsContext();

  const [isLargeScreen] = useMediaQuery("(min-width: 764px)");
  const [selectedBg, setSelectedBg] = useState<number | string>(0);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);

  const onSave = async (selectedBackground: number) => {
    setProfileData({
      ...profileData,
      bg: selectedBackground,
    });

    if (sdk) {
      const res = await sdk.getDataManager().saveSettings({
        data: {
          bg: selectedBackground,
        },
      });
    }
  };

  const getProfile = async () => {
    try {
      if (sdk && isLoggedIn) {
        const data = await sdk.getDataManager().getUserDoc();
        const settings = await sdk.getDataManager().getSettings();
        if (data || settings) {
          const annualWordList = settings?.annualWordList;
          const annualExerciseCount = settings?.annualExerciseCount;
          setProfileData({
            username: data.nickname,
            username_fr: data.pseudonymConfig?.nickname_fr ?? data.nickname_fr,
            level: getFormattedLevel(data.level),
            totalSuccessScore: "",
            successRate: 75,
            finishedexercises: "",
            annualWordList: `${(annualWordList) ? annualWordList?.length : 0}`,
            annualExerciseCount: `${(annualExerciseCount) ? annualExerciseCount : 0}`,
            bg: settings && settings.bg ? settings.bg : 1,
            photoUrl: data.photo_url,
            type: data.type
          });
          setAvatar(data.photo_url);
          setSelectedBg(settings && settings.bg ? settings.bg : 1);
        }
      }
    } catch (err) {
      console.log(err)
    }
  };

  useEffect(() => {
    if (sdk) {
      setIsLoggedIn(sdk._firebase.isLoggedIn());
      getProfile();
    }
  }, [sdk, isLoggedIn]);

  useEffect(() => {
    getProfile();
  }, [profileOpen]);

  const setBackground = (bg: number) => {
    setSelectedBg(bg);
    onSave(bg);
  };

  const tabs: JSX.Element[] = [
    <APFormattedMessage id={"profile"} />,
    <APFormattedMessage id={"wallpapers"} />,
  ]

  return (
    <Drawer
      isOpen={profileOpen}
      onClose={() => setProfileOpen(false)}
      size="sm">
      <DrawerOverlay />
      <DrawerContent borderRadius={0} overflow={"hidden"}>
        <DrawerBody padding={0}>
          <HStack paddingTop={["0.75rem", "1rem", "1.25rem"]} paddingX={["0.75rem", "1rem", "1.25rem"]}>
            {isLoggedIn ?
              (<Flex width={"100%"} justifyContent={"space-between"} alignItems={"center"} gap={[2, 4]}>
                <Avatar
                  src={
                    avatar && avatar !== "0" ?
                      `avatars/${avatar}.svg` : profileData ? undefined : `${process.env.REACT_APP_BASE_URL}/avatars/fire.svg`
                  }
                  name={locale === "fr" ? profileData?.username_fr : profileData?.username}
                  width={["3rem", "4rem"]}
                  height={["3rem", "4rem"]}
                />
                <Flex flexDirection={"column"} alignItems={"start"} width={"100%"}>
                  <Text fontSize={"lg"} fontWeight={"bold"}>
                    {profileData?.username}
                  </Text>
                  <Text fontSize={"md"} fontWeight={"normal"} sx={{ margin: 0 }}>
                    {profileData?.type === "student" && profileData?.level}
                    {profileData?.type === "teacher" && <APFormattedMessage id={"teacher"} />}
                    {profileData?.type === "parent" && <APFormattedMessage id={"parent"} />}
                  </Text>
                </Flex>
              </Flex>) :
              <Spacer />}
            <APIconButton variant={"raisedIcon"} rounded={"full"} onClick={() => setProfileOpen(false)}
                          fontSize={"1.5rem"}
                          aria-label={getFormattedString('aria-label-close-modal')}>
              <Close />
            </APIconButton>
          </HStack>
          <Tabs variant="enclosed" colorScheme={"tertiary"} defaultIndex={defaultProfileTab} padding={"1rem"}
                onChange={setSettingsTabIndex}>
            <TabList>
              {tabs.map((tab, index) => (
                <Tab key={index} fontWeight={"bold"}>
                  {tab}
                </Tab>
              ))}
            </TabList>
            <TabPanels>
              <TabPanel key={"profile"}>
                <ProfileView
                  profile={profileData}
                  isLoggedIn={isLoggedIn}
                  sessionData={sessionData}
                />
              </TabPanel>
              <TabPanel key={"wallpaper"}>
                <WallpapersView
                  setBackground={setBackground}
                  background={selectedBg}
                  isLoggedIn={sdk?._firebase?.isLoggedIn() ?? false}
                  sdk={sdk}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

export default Profile;
